import {
  isValidIconName,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSIconName,
  WSImage,
  WSInfoBox,
  WSList,
  WSMessageBox,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSPillProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { ServiceState } from "@wingspanhq/users/dist/lib/interfaces/enabledServices";
import qs from "qs";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { WSQueries } from "../../../query/WSQuery";
import {
  useBenefitsEnrollment,
  useBenefitsService
} from "../../../query/benefits";
import { useUserId } from "../../../query/hooks/helpers";
import { useCreateOrUpdateSubscriptionV3 } from "../../../query/subscriptions/mutations";
import { useUpdateActivity } from "../../../query/users/mutations";
import { useMemberProfile } from "../../../query/users/queries";
import { selectorMemberCompany } from "../../../shared/selectors/selectorMemberCompany";
import { selectorMemberLegalBusinessName } from "../../../shared/selectors/selectorMemberLegalBusinessName";
import { openInNewTab } from "../../../utils/openInNewTab";
import { PATH_BENEFITS_ENROLL } from "../paths";
import { ExtendedWebflowBenefit } from "../types";
import {
  getCostOfBenefitWithSubscription,
  insertMemberAndGroupIdsIntoText,
  isBenefitIncludedInSubscriptionLevel
} from "../utils";
import styles from "./BenefitsCMSPage.module.scss";

interface BenefitsCMSPageProps {
  benefit: ExtendedWebflowBenefit;
  isFree?: boolean;
  noPill?: boolean;
  parentPath?: string;
}

export const BenefitsCMSPage: React.FC<BenefitsCMSPageProps> = ({
  benefit,
  isFree,
  noPill,
  parentPath
}) => {
  const history = useHistory();
  const location = useLocation();

  const [isFAQSExpanded, setIsFAQSExpanded] = useState(false);

  const userId = useUserId();
  const qEnrollment = useBenefitsEnrollment(userId);
  const qService = useBenefitsService();
  const qMember = useMemberProfile(userId);

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [createOrUpdateSubscription, createOrUpdateSubscriptionMeta] =
    useCreateOrUpdateSubscriptionV3(userId);

  let pill: WSPillProps | undefined = undefined;

  useBrowserPageTitle(`Benefits - ${benefit.mainHeader}`);

  return (
    <WSQueries queries={{ qEnrollment, qService, qMember }}>
      {({ qEnrollment, qService, qMemberData }) => {
        const {
          category,
          logo,
          featured,
          icon,
          mainCards,
          orderPriority,
          helperContactInfoDescription,
          helperContactInfoEmail,
          helperContactInfoPhone,
          helperCostDescription,
          helperCTAPrimaryButtonLink,
          helperCTACustomButton,
          helperCTAPrimaryButtonText,
          helperHeader,
          helperHideMemberAndGroupInfo,
          helperLogo,
          helperUsageText,
          isDraft,
          itemId,
          mainDescription,
          mainDescriptionList,
          mainFAQSHeader,
          mainFAQSItems,
          mainFootnote,
          mainHeader,
          mainInfoListHeader,
          mainInfoListItems,
          mainLearnMoreLink,
          mainLearnMoreLinkText,
          mainSubheader,
          mainVideo,
          minSubscription,
          minSubscriptionPackage,
          minSubscriptionPackageTier,
          name,
          publishedOn,
          slug,
          customEnrollLink
        } = benefit;

        const hasHelper = !!(
          helperContactInfoDescription ||
          helperContactInfoEmail ||
          helperContactInfoPhone ||
          helperCTAPrimaryButtonLink ||
          helperCTAPrimaryButtonText ||
          helperHeader ||
          helperLogo?.src ||
          helperUsageText ||
          !helperHideMemberAndGroupInfo
        );

        const searchParams = qs.stringify({
          first_name: qMemberData.user.profile.firstName,
          last_name: qMemberData.user.profile.lastName,
          phone: qMemberData.user.phone.number?.substring(2),
          email: qMemberData.user.email,
          company_name:
            selectorMemberCompany(qMemberData) ||
            selectorMemberLegalBusinessName(qMemberData),
          line_1: qMemberData.profile.address?.addressLine1,
          line_2: qMemberData.profile.address?.addressLine2,
          city: qMemberData.profile.address?.city,
          state: qMemberData.profile.address?.state,
          zip: qMemberData.profile.address?.postalCode
        });

        const primaryButtonLink = customEnrollLink
          ? customEnrollLink
          : helperCTAPrimaryButtonLink
          ? helperCTAPrimaryButtonLink.includes("?")
            ? `${helperCTAPrimaryButtonLink}&${searchParams}`
            : `${helperCTAPrimaryButtonLink}?${searchParams}`
          : helperCTAPrimaryButtonLink;

        const memberNumber =
          qEnrollment.data?.wingspanBenefits?.memberNumber || "--";
        const groupNumber =
          qEnrollment.data?.wingspanBenefits?.groupNumber || "--";

        const needsUpgrade = !isBenefitIncludedInSubscriptionLevel(benefit);
        if (needsUpgrade) {
          pill = {
            text: "Premium",
            theme: "neutral",
            icon: "lock"
          };
        } else if (!benefit.hideCost) {
          pill = {
            text: "Included",
            theme: "success"
          };
        }

        const isPending = qService.data.serviceState === ServiceState.Pending;
        const isEnabled = qService.data.serviceState === ServiceState.Enabled;

        const isNotReady = !isEnabled && !isPending;

        let primaryButton: WSPageToolbarAction | undefined;

        let showHelperCTAAndContactInfo = false;

        if (customEnrollLink) {
          primaryButton = {
            label: "Enroll now",
            buttonKind: "Primary",
            onClick: () => {
              openInNewTab(customEnrollLink);
            }
          };
        } else if (isFree) {
          showHelperCTAAndContactInfo = true;
        } else if (isNotReady) {
          primaryButton = {
            label: "Enroll now",
            buttonKind: "Primary",
            loading:
              createOrUpdateSubscriptionMeta.isLoading ||
              updateActivityMeta.isLoading,
            onClick: () => {
              history.push({
                pathname: PATH_BENEFITS_ENROLL,
                state: {
                  backPath: location.pathname
                }
              });
            }
          };
        } else if (needsUpgrade) {
          primaryButton = {
            buttonKind: "Primary",
            label: "Upgrade membership",
            onClick: async () => {
              history.push("/member/membership-plans");
            }
          };
        } else if (isPending) {
          primaryButton = {
            buttonKind: "Primary",
            label: "Pending",
            disabled: true
          };
        } else if (isEnabled) {
          showHelperCTAAndContactInfo = true;
        }

        return (
          <WSPage colorBackground="gray50">
            <WSPageToolbar
              breadcrumbs={[
                {
                  label: "Benefits",
                  onClick() {
                    parentPath ? history.push(parentPath) : history.goBack();
                  }
                },
                {
                  label: mainHeader
                }
              ]}
              title={mainHeader}
              titlePill={noPill ? undefined : pill}
              actions={primaryButton ? [primaryButton] : undefined}
            />
            <WSGrid data-testid="benefitContent">
              <WSGrid.Item span={{ s: "8" }}>
                <WSCard>
                  <WSList gap="XL">
                    {mainSubheader && (
                      <WSText.Heading5 mb="XL">{mainSubheader}</WSText.Heading5>
                    )}
                    {mainVideo && mainVideo.src && (
                      <WSElement className={styles.videoContainer}>
                        <iframe
                          className={styles.video}
                          frameBorder="0"
                          allowFullScreen
                          title={mainVideo.title || mainHeader}
                          src={mainVideo.src}
                        ></iframe>
                      </WSElement>
                    )}
                    {mainDescription && (
                      <WSElement>
                        {typeof mainDescription === "string"
                          ? mainDescription
                              .split("\n")
                              .map((descriptionLine, index) => (
                                <WSText
                                  key={index}
                                  mt={index === 0 ? "NONE" : "M"}
                                >
                                  {descriptionLine}
                                </WSText>
                              ))
                          : mainDescription}
                      </WSElement>
                    )}
                    {mainDescriptionList && (
                      <WSElement as="ul" ml="XL">
                        {mainDescriptionList.split("\n").map((item, index) => (
                          <WSElement key={index} as="li">
                            <WSText mt={index === 0 ? "NONE" : "XS"}>
                              {item}
                            </WSText>
                          </WSElement>
                        ))}
                      </WSElement>
                    )}
                    {mainCards && (
                      <WSElement>
                        <WSGrid>
                          {mainCards.split("\n-\n").map((card, index) => {
                            const cardParts = card.split("\n");
                            if (cardParts.length > 0) {
                              let icon: WSIconName = "check-envelope";
                              let textParts: string[] = [];
                              if (cardParts.length === 1) {
                                textParts = cardParts;
                              } else {
                                if (isValidIconName(cardParts[0])) {
                                  icon = cardParts[0];
                                  textParts = cardParts.slice(1);
                                } else {
                                  textParts = cardParts;
                                }
                              }
                              return (
                                <WSGrid.Item
                                  span={{ xs: "6", l: "4" }}
                                  key={card + index}
                                >
                                  <WSCard
                                    style={{ height: "100%" }}
                                    header={{
                                      label: {
                                        avatar: {
                                          type: "icon",
                                          colorBackground: "blue50",
                                          color: "blue500",
                                          icon: icon
                                        }
                                      }
                                    }}
                                  >
                                    <WSList gap="S">
                                      {textParts.map(text => (
                                        <WSText.ParagraphSm
                                          color="gray600"
                                          key={text}
                                        >
                                          {text}
                                        </WSText.ParagraphSm>
                                      ))}
                                    </WSList>
                                  </WSCard>
                                </WSGrid.Item>
                              );
                            }
                            return undefined;
                          })}
                        </WSGrid>
                      </WSElement>
                    )}

                    {mainInfoListItems && (
                      <WSElement>
                        <WSText.Heading4 mb="XL">
                          {mainInfoListHeader
                            ? mainInfoListHeader
                            : "How it works"}
                        </WSText.Heading4>
                        {mainInfoListItems
                          .split("\n-\n")
                          .map((listItem, index) => {
                            const number = index + 1;
                            const splitItems = listItem.split("\n");
                            if (splitItems.length > 0) {
                              return (
                                <WSFlexBox
                                  key={index}
                                  mt={index === 0 ? "NONE" : "XL"}
                                  wrap="nowrap"
                                >
                                  <WSElement>
                                    <WSText weight="medium" mr="M">
                                      {number}.
                                    </WSText>
                                  </WSElement>
                                  <WSElement>
                                    <WSText weight="medium">
                                      {splitItems[0]}
                                    </WSText>
                                    {splitItems.slice(1).map((item, index) => (
                                      <WSText key={index}>{item}</WSText>
                                    ))}
                                  </WSElement>
                                </WSFlexBox>
                              );
                            }
                            return undefined;
                          })}
                      </WSElement>
                    )}

                    {mainFAQSItems && (
                      <WSElement>
                        <WSFlexBox
                          gap="L"
                          alignItems="center"
                          onClick={() => setIsFAQSExpanded(!isFAQSExpanded)}
                        >
                          <WSText.Heading4>
                            {mainFAQSHeader ? mainFAQSHeader : "FAQs"}
                          </WSText.Heading4>
                          <WSIcon
                            name={isFAQSExpanded ? "caret-up" : "caret-down"}
                          />
                        </WSFlexBox>

                        {isFAQSExpanded && (
                          <WSElement mt="L">
                            {typeof mainFAQSItems === "string"
                              ? mainFAQSItems
                                  .split("\n-\n")
                                  .map((listItem, index) => {
                                    const splitItems = listItem.split("\n");
                                    if (splitItems.length >= 2) {
                                      return (
                                        <WSElement
                                          key={index}
                                          mt={index === 0 ? "NONE" : "XL"}
                                        >
                                          <WSText weight="medium" mb="XS">
                                            {splitItems[0]}
                                          </WSText>
                                          {splitItems
                                            .slice(1)
                                            .map((item, index) => (
                                              <WSText.ParagraphSm
                                                color="gray600"
                                                key={index}
                                              >
                                                {item}
                                              </WSText.ParagraphSm>
                                            ))}
                                        </WSElement>
                                      );
                                    }
                                    return undefined;
                                  })
                              : mainFAQSItems}
                          </WSElement>
                        )}
                      </WSElement>
                    )}
                    <WSElement>
                      {mainLearnMoreLink && (
                        <WSButton.Link
                          onClick={() => openInNewTab(mainLearnMoreLink)}
                          rightIcon="open-tab"
                        >
                          {mainLearnMoreLinkText
                            ? mainLearnMoreLinkText
                            : `Learn more about ${name}...`}
                        </WSButton.Link>
                      )}
                    </WSElement>
                    {mainFootnote && (
                      <WSText color="gray500">{mainFootnote}</WSText>
                    )}
                  </WSList>
                </WSCard>
              </WSGrid.Item>
              {hasHelper && (
                <WSGrid.Item span={{ s: "4" }}>
                  <WSCard>
                    <WSList gap="XL">
                      {helperHeader && (
                        <WSElement
                          className={styles.helperHeaderContainer}
                          p="S"
                        >
                          <WSText align="center" color="gray600">
                            {helperHeader}
                          </WSText>
                        </WSElement>
                      )}
                      {helperLogo?.src && (
                        <WSImage
                          src={helperLogo.src}
                          placeholder={helperLogo.placeholder}
                        />
                      )}

                      {!benefit.hideCost && (
                        <WSInfoBox theme="neutral">
                          Cost: {getCostOfBenefitWithSubscription(benefit)}
                        </WSInfoBox>
                      )}

                      {helperUsageText && (
                        <WSText>
                          {insertMemberAndGroupIdsIntoText(
                            helperUsageText,
                            memberNumber,
                            groupNumber
                          )}
                        </WSText>
                      )}

                      {showHelperCTAAndContactInfo && (
                        <>
                          {helperCTACustomButton ? (
                            helperCTACustomButton
                          ) : helperCTAPrimaryButtonText &&
                            primaryButtonLink ? (
                            <WSButton
                              fullWidth
                              onClick={() => {
                                openInNewTab(primaryButtonLink);
                              }}
                            >
                              {helperCTAPrimaryButtonText}
                            </WSButton>
                          ) : null}
                          {helperContactInfoDescription && (
                            <WSText>{helperContactInfoDescription}</WSText>
                          )}
                          {(helperContactInfoEmail ||
                            helperContactInfoPhone) && (
                            <WSMessageBox kind="Regular" noBorder>
                              {(() => {
                                let instruction = "Contact";
                                if (
                                  helperContactInfoPhone &&
                                  helperContactInfoEmail
                                ) {
                                  instruction = "Call or email";
                                } else if (helperContactInfoEmail) {
                                  instruction = "Email";
                                } else {
                                  instruction = "Call";
                                }
                                return (
                                  <>
                                    <WSText mb="XS">{instruction}</WSText>
                                    {helperContactInfoPhone && (
                                      <WSText.Heading4
                                        onClick={() =>
                                          openInNewTab(
                                            `tel:${helperContactInfoPhone.replace(
                                              /\D/g,
                                              ""
                                            )}`
                                          )
                                        }
                                        mb={
                                          helperContactInfoEmail ? "XS" : "NONE"
                                        }
                                      >
                                        {helperContactInfoPhone}
                                      </WSText.Heading4>
                                    )}
                                    {helperContactInfoEmail && (
                                      <WSButton.Link
                                        onClick={() =>
                                          window.open(
                                            `mailto:${helperContactInfoEmail}`
                                          )
                                        }
                                      >
                                        {helperContactInfoEmail}
                                      </WSButton.Link>
                                    )}
                                  </>
                                );
                              })()}
                            </WSMessageBox>
                          )}
                        </>
                      )}
                      {!helperHideMemberAndGroupInfo && (
                        <WSElement>
                          <WSText.ParagraphSm color="gray500">
                            Your Member ID
                          </WSText.ParagraphSm>
                          <WSText mt="XS">{memberNumber}</WSText>
                          <WSText.ParagraphSm color="gray500" mt="XL">
                            Your Group ID
                          </WSText.ParagraphSm>
                          <WSText mt="XS">{groupNumber}</WSText>
                        </WSElement>
                      )}
                    </WSList>
                  </WSCard>
                </WSGrid.Item>
              )}
            </WSGrid>
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
